import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Cookies from "js-cookie";
import { useAuth } from "./auth";
import { getEndpoint } from "../utils/stringUtil";
import type { AppNotification } from "../api/Notifications";

// The axios-based API clients are imported DYNAMICALLY (mirroring
// usePrecompiledProgressionName) so this provider — which is mounted at the top
// of the app tree — doesn't drag axios into the static module graph, which
// would break transform-free jest suites (e.g. App.test).
const loadChatApi = () => import("../api/Chat").then((m) => m.Chat);
const loadNotificationsApi = () => import("../api/Notifications").then((m) => m.Notifications);

// Live message shape pushed by the BE websocket (middlewares/webSocket.ts).
export interface RealtimeMessage {
  type: "new_chat" | "new_conversation" | "notification" | "notification_count";
  data: any;
}

interface RealtimeContextInterface {
  connected: boolean;
  chatUnread: number;
  notifUnread: number;
  notifications: AppNotification[];
  refreshCounts: () => void;
  refreshNotifications: () => void;
  markNotificationRead: (id: number) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  removeNotification: (id: number) => Promise<void>;
  // Subscribe to raw WS messages (used by the chat widget to live-refresh an
  // open thread). Returns an unsubscribe fn.
  subscribe: (handler: (msg: RealtimeMessage) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextInterface>(
  {} as RealtimeContextInterface
);

const wsUrlFromEndpoint = (token: string): string => {
  // getEndpoint() -> http(s)://host/ ; the WS server rides the same http
  // server (server.on('upgrade')) so we swap the scheme and pass ?token=.
  const httpBase = getEndpoint() || "";
  const wsBase = httpBase.replace(/^http/i, "ws");
  const sep = wsBase.includes("?") ? "&" : "?";
  return `${wsBase}${sep}token=${encodeURIComponent(token)}`;
};

export const RealtimeProvider = ({ children }: React.PropsWithChildren) => {
  const { loggedUser, setNewNotifications } = useAuth();
  const [connected, setConnected] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscribersRef = useRef<Set<(msg: RealtimeMessage) => void>>(new Set());
  const shouldCloseRef = useRef(false);

  const refreshCounts = useCallback(() => {
    if (!loggedUser?.id) return;
    const uid = loggedUser.id;
    loadChatApi()
      .then((Chat) => Chat.getUnread(uid))
      .then((res) => setChatUnread(res.data.unread ?? 0))
      .catch(() => {});
    loadNotificationsApi()
      .then((Notifications) => Notifications.unread())
      .then((res) => setNotifUnread(res.data.unread ?? 0))
      .catch(() => {});
  }, [loggedUser?.id]);

  const refreshNotifications = useCallback(() => {
    if (!loggedUser?.id) return;
    loadNotificationsApi()
      .then((Notifications) => Notifications.list())
      .then((res) => {
        setNotifications(res.data.list ?? []);
        setNotifUnread(res.data.unread ?? 0);
      })
      .catch(() => {});
  }, [loggedUser?.id]);

  const markNotificationRead = useCallback(async (id: number) => {
    try {
      const Notifications = await loadNotificationsApi();
      const res = await Notifications.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setNotifUnread(res.data.unread ?? 0);
    } catch (_) {}
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    try {
      const Notifications = await loadNotificationsApi();
      await Notifications.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotifUnread(0);
    } catch (_) {}
  }, []);

  const removeNotification = useCallback(async (id: number) => {
    try {
      const Notifications = await loadNotificationsApi();
      const res = await Notifications.remove(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setNotifUnread(res.data.unread ?? 0);
    } catch (_) {}
  }, []);

  const subscribe = useCallback((handler: (msg: RealtimeMessage) => void) => {
    subscribersRef.current.add(handler);
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);

  // Keep the AuthContext badge (used by the navbar) in sync with our combined
  // unread total.
  useEffect(() => {
    setNewNotifications?.(chatUnread + notifUnread);
  }, [chatUnread, notifUnread, setNewNotifications]);

  // Establish (and keep alive) the websocket while logged in.
  useEffect(() => {
    const token = Cookies.get(
      window.__env.AUTH_TOKEN_NAME || "coaching-master-token"
    );
    if (!loggedUser?.id || !token) return;

    shouldCloseRef.current = false;

    const connect = () => {
      if (shouldCloseRef.current) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrlFromEndpoint(token));
      } catch (_) {
        return;
      }
      socketRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Prime counts on (re)connect.
        refreshCounts();
      };

      ws.onmessage = (event) => {
        let msg: RealtimeMessage;
        try {
          msg = JSON.parse(event.data);
        } catch (_) {
          return;
        }
        if (msg.type === "notification_count") {
          setNotifUnread(Number(msg.data) || 0);
        } else if (msg.type === "notification") {
          setNotifications((prev) => [msg.data, ...prev]);
        } else if (msg.type === "new_chat") {
          // A message landed in one of our conversations — refresh the badge.
          if (loggedUser?.id) {
            const uid = loggedUser.id;
            loadChatApi()
              .then((Chat) => Chat.getUnread(uid))
              .then((res) => setChatUnread(res.data.unread ?? 0))
              .catch(() => {});
          }
        }
        // Fan out to component subscribers (e.g. the open chat thread).
        subscribersRef.current.forEach((h) => {
          try {
            h(msg);
          } catch (_) {}
        });
      };

      ws.onclose = () => {
        setConnected(false);
        socketRef.current = null;
        if (!shouldCloseRef.current) {
          reconnectRef.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {
        // Let onclose drive the reconnect.
        try {
          ws.close();
        } catch (_) {}
      };
    };

    connect();
    // Initial data.
    refreshNotifications();

    return () => {
      shouldCloseRef.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch (_) {}
      }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser?.id]);

  return (
    <RealtimeContext.Provider
      value={{
        connected,
        chatUnread,
        notifUnread,
        notifications,
        refreshCounts,
        refreshNotifications,
        markNotificationRead,
        markAllNotificationsRead,
        removeNotification,
        subscribe,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);
